import * as registrationApi from "app/api/registrationApi";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import Recaptcha from "app/screens/AuthScreen/components/Recaptcha/Recaptcha";
import { ListOfCurrenciesType } from "app/types/registrationTypes";
import { RegistrationFormScreens } from "app/utils/const";
import { useEffect, useState } from "react";
import { FormattedMessage, injectIntl, IntlShape } from "react-intl";
import { connect } from "react-redux";
import "./AccountSettings.scss";
import ConsentToMarketing from "./components/ConsentToMarketing/ConsentToMarketing";
import ConsentToTerms from "./components/ConsentToTerms/ConsentToTerms";
import Currency from "./components/Currency/Currency";
import HeardAbout from "./components/HeardAbout/HeardAbout";
import PromoCode from "./components/PromoCode/PromoCode";
import ValidationErrorMessage from "./components/ValidationErrorMessage/ValidationErrorMessage";
//
type AccountSettingsProps = {
  intl: IntlShape;
  handleVerify: Function;
  handleExpire: Function;
  handleChange: (id: string, value: string | boolean) => void;
  setSelectedScreen: (selectedScreen: string) => void;
  currency: string;
  heardAbout: string;
  promoCode: string;
  consentToMarketing: boolean;
  consentToTermsAndConditions: boolean;
  submitRegistration: () => void;
  gRecaptchaResponse: string;
  listOfCurrencies: ListOfCurrenciesType;
  listOfHeardAbout: {
    id: string;
    name: string;
  }[];
  heardAboutOther: string;
  expired: boolean;
  successfulRegistration: boolean;
  selectedScreen: string;
  isSubmitting: boolean;
  setSuccessfulRegistration: (isSuccessfulRegistration: boolean) => void;
  hasRegistrationError: boolean;
  registrationErrorMessage: string;
  resetRegistrationError: () => void;
  setIsUserRegistered: (isUserRegistered: boolean) => void;
};

const AccountSettings = (props: AccountSettingsProps): JSX.Element => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [currencyValidationError, setCurrencyValidationError] = useState(false);
  const [heardAboutValidationError, setHeardAboutValidationError] =
    useState(false);
  const [promoCodeValidationError, setPromoCodeValidationError] =
    useState(false);
  const [
    consentToMarketingValidationError,
    setConsentToMarketingValidationError,
  ] = useState(false);
  const [
    consentToTermsAndConditionsValidationError,
    setConsentToTermsAndConditionsValidationError,
  ] = useState(false);
  const [
    accountSettingsValidationErrorMessage,
    setAccountSettingsValidationErrorMessage,
  ] = useState("");
  const [accountSettingsValidated, setAccountSettingsValidated] =
    useState(false);

  const processValidationResults = (validationResponse: any) => {
    if (validationResponse.valid) {
      setAccountSettingsValidationErrorMessage("");
      setAccountSettingsValidated(validationResponse.valid);
      setCurrencyValidationError(false);
      setHeardAboutValidationError(false);
      setPromoCodeValidationError(false);
      setConsentToMarketingValidationError(false);
      setConsentToTermsAndConditionsValidationError(false);
      setErrors({});
    } else {
      // check all validation fields and set the error messages
      if (!validationResponse.currencyValid) {
        setCurrencyValidationError(true);
      }
      if (!validationResponse.heardAboutValid) {
        setHeardAboutValidationError(true);
      }
      if (!validationResponse.promoCodeValid) {
        setPromoCodeValidationError(true);
      }
      if (!validationResponse.consentToMarketingValid) {
        setConsentToMarketingValidationError(true);
      }
      if (!validationResponse.consentToTermsAndConditionsValid) {
        setConsentToTermsAndConditionsValidationError(true);
      }
      if (!validationResponse.valid) {
        setAccountSettingsValidated(false);
      }
      if (validationResponse.errorMessage) {
        setAccountSettingsValidationErrorMessage(
          validationResponse.errorMessage
        );
      }
    }
  };

  const validateAccountSettings = () => {
    // validate all form details and set the errors
    // if all fields are validated then validate account setting using API
    registrationApi
      .validateAccountSettings({
        currency: props.currency,
        heardAboutUs: props.heardAbout,
        promoCode: props.promoCode,
        consentToMarketing: props.consentToMarketing,
        acceptedTCs: props.consentToTermsAndConditions,
      })
      .then(response => {
        if (response.data) {
          processValidationResults(response.data);
        } else {
          setAccountSettingsValidationErrorMessage(
            props.intl.formatMessage({
              id: "error-contact-support",
            })
          );
          setAccountSettingsValidated(false);
        }
      })
      .catch(() => {
        setAccountSettingsValidationErrorMessage(
          props.intl.formatMessage({
            id: "error-contact-support",
          })
        );
        setAccountSettingsValidated(false);
      });
  };

  useEffect(() => {
    if (accountSettingsValidated) {
      props.resetRegistrationError();
      props.submitRegistration();
    }
  }, [accountSettingsValidated]);

  useEffect(() => {
    if (props.hasRegistrationError) {
      setAccountSettingsValidationErrorMessage(
        props.registrationErrorMessage ||
          props.intl.formatMessage({ id: "error-contact-support" })
      );
      setAccountSettingsValidated(false);
    } else if (!props.successfulRegistration) {
      setAccountSettingsValidationErrorMessage("");
    }
    // If registration is successful, update modal progress
    if (props.successfulRegistration) {
      props.setIsUserRegistered(true);
    }
  }, [
    props.hasRegistrationError,
    props.registrationErrorMessage,
    props.successfulRegistration,
    props.intl,
  ]);

  if (props.selectedScreen === RegistrationFormScreens.ACCOUNT_SETTINGS) {
    return (
      <div className="registration-form-modal__account-settings">
        {!props.successfulRegistration && (
          <div className="form-details">
            <Currency
              intl={props.intl}
              currency={props.currency}
              handleChange={props.handleChange}
              currencyValidated={true}
              currencyValidationError={currencyValidationError}
              setCurrencyValidationError={setCurrencyValidationError}
              listOfCurrencies={props.listOfCurrencies}
              errors={errors}
            />
            <HeardAbout
              intl={props.intl}
              heardAbout={props.heardAbout}
              handleChange={props.handleChange}
              heardAboutValidationError={heardAboutValidationError}
              setHeardAboutValidationError={setHeardAboutValidationError}
              listOfHeardAbout={props.listOfHeardAbout}
              heardAboutOther={props.heardAboutOther}
            />
            <PromoCode
              intl={props.intl}
              promoCode={props.promoCode}
              handleChange={props.handleChange}
              promoCodeValidationError={promoCodeValidationError}
            />
            <div className="form-details__row">
              <ConsentToMarketing
                consentToMarketing={props.consentToMarketing}
                handleChange={props.handleChange}
              />
              <ConsentToTerms
                intl={props.intl}
                consentToTermsAndConditions={props.consentToTermsAndConditions}
                handleChange={props.handleChange}
                consentToMarketingValidationError={
                  consentToMarketingValidationError
                }
              />
            </div>
            <div
              className={`form-details__row form-details__recaptcha ${
                props.expired ? "recaptcha-expired" : ""
              }`}
            >
              <Recaptcha
                onVerify={props.handleVerify}
                onExpire={props.handleExpire}
              />
            </div>
            {accountSettingsValidationErrorMessage && (
              <ValidationErrorMessage
                accountSettingsValidationErrorMessage={
                  accountSettingsValidationErrorMessage
                }
              />
            )}
          </div>
        )}

        {props.successfulRegistration && (
          <div
            className={`form-details__row form-details__row--success-message`}
          >
            <FormattedMessage
              tagName=""
              id="home-account-register-check-email"
            />
          </div>
        )}
        {!props.successfulRegistration && (
          <div className="continue-button-container">
            <button
              className="nav-bar__nav-item--contact nav-bar__nav-item--back--button"
              onClick={() => {
                props.setSelectedScreen(
                  RegistrationFormScreens.PERSONAL_DETAILS
                );
              }}
            >
              <FormattedMessage
                tagName="div"
                id="account-verification-select-document-back-button"
              />
            </button>
            <button
              className="nav-bar__nav-item--contact nav-bar__nav-item--contact--button"
              onClick={() => {
                validateAccountSettings();
              }}
            >
              <FormattedMessage tagName="div" id="home-cta-register" />
              {props.isSubmitting && <LoadingSpinner />}
            </button>
          </div>
        )}
      </div>
    );
  }
};

const mapStateToProps = state => ({
  listOfCurrencies: state.registrationFormModel.listOfCurrencies,
  listOfHeardAbout: state.registrationFormModel.listOfHeardAbout,
  successfulRegistration: state.registrationFormModel.successfulRegistration,
  isSubmitting: state.registrationFormModel.isSubmitting,
  hasRegistrationError: state.registrationFormModel.hasRegistrationError,
  registrationErrorMessage:
    state.registrationFormModel.registrationErrorMessage,
});

const mapDispatchToProps = dispatch => ({
  setSuccessfulRegistration: (isSuccessfulRegistration: boolean) =>
    dispatch.registrationFormModel.submitRegistrationForm(
      isSuccessfulRegistration
    ),
  resetRegistrationError: () =>
    dispatch.registrationFormModel.resetRegistrationError(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(AccountSettings));
